import { useEffect, useState, useCallback } from "react";
import { Vault, ArrowDownLeft, ArrowUpRight, PencilSimple, Trash, FileXls, FilePdf, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/format";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INCOME_CATS = ["Satış", "Hizmet", "Faiz", "Tahsilat", "Diğer Gelir"];
const EXPENSE_CATS = ["Maaş", "Kira", "Tedarik", "Elektrik/Su", "Pazarlama", "Vergi", "Lojistik", "Ödeme", "Diğer"];
const catsFor = (type, current) => {
  const base = type === "income" ? INCOME_CATS : EXPENSE_CATS;
  return current && !base.includes(current) ? [current, ...base] : base;
};

export default function KasaDetay() {
  const { currencies } = useCurrency();
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState("");
  const [data, setData] = useState({ account: null, transactions: [] });
  const [filter, setFilter] = useState({ start: "", end: "", type: "all" });
  const [txEdit, setTxEdit] = useState(null);
  const [countOpen, setCountOpen] = useState(false);
  const [counted, setCounted] = useState("");
  const [countNote, setCountNote] = useState("");
  const [counts, setCounts] = useState([]);

  const loadCounts = useCallback((id) => {
    if (!id) return setCounts([]);
    api.get(`/accounts/${id}/counts`).then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/accounts").then((r) => {
      setAccounts(r.data);
      if (r.data.length) {
        const kasa = r.data.find((a) => a.type === "cash") || r.data[0];
        setAccId(kasa.id);
      }
    }).catch(() => {});
  }, []);

  const fetchMovements = useCallback(async (id, f) => {
    if (!id) return;
    const params = new URLSearchParams();
    if (f.start) params.set("start", f.start);
    if (f.end) params.set("end", f.end);
    if (f.type && f.type !== "all") params.set("type", f.type);
    const qs = params.toString();
    try {
      const r = await api.get(`/accounts/${id}/transactions${qs ? `?${qs}` : ""}`);
      setData(r.data);
    } catch (e) { toast.error("Hareketler yüklenemedi"); }
  }, []);

  useEffect(() => { fetchMovements(accId, filter); }, [accId, filter, fetchMovements]);

  const applyFilter = (patch) => setFilter((f) => ({ ...f, ...patch }));

  const income = data.transactions.filter((t) => t.type === "income").reduce((s, t) => s + (t.amount || 0), 0);
  const expense = data.transactions.filter((t) => t.type === "expense").reduce((s, t) => s + (t.amount || 0), 0);
  const cur = data.account?.currency || "TRY";
  const opening = data.opening_balance ?? 0;
  const closing = data.closing_balance ?? (data.account?.balance ?? 0);
  const periodNet = data.period_net ?? 0;

  const openTxEdit = (t) => setTxEdit({ id: t.id, amount: String(t.amount), description: t.description || "", date: (t.date || "").slice(0, 10), type: t.type, category: t.category || "" });
  const setTxType = (v) => setTxEdit((f) => {
    const cats = v === "income" ? INCOME_CATS : EXPENSE_CATS;
    return { ...f, type: v, category: cats.includes(f.category) ? f.category : cats[0] };
  });
  const saveTxEdit = async () => {
    if (!txEdit.amount || Number(txEdit.amount) <= 0) return toast.error("Geçerli tutar girin");
    try {
      await api.put(`/transactions/${txEdit.id}`, {
        amount: Number(txEdit.amount), description: txEdit.description,
        date: txEdit.date ? new Date(txEdit.date).toISOString() : null,
        type: txEdit.type, category: txEdit.category,
      });
      toast.success("Hareket güncellendi");
      setTxEdit(null);
      fetchMovements(accId, filter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi"); }
  };
  const removeTx = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Hareket silindi");
      fetchMovements(accId, filter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi"); }
  };
  const isEditable = (t) => !t.transfer_group && !t.payment_id;

  const openCount = () => { setCounted(""); setCountNote(""); loadCounts(accId); setCountOpen(true); };
  const saveCount = async () => {
    if (counted === "" || Number(counted) < 0) return toast.error("Sayılan tutarı girin");
    try {
      await api.post(`/accounts/${accId}/counts`, { counted: Number(counted), note: countNote });
      toast.success("Sayım kaydedildi");
      setCounted(""); setCountNote("");
      loadCounts(accId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi"); }
  };
  const removeCount = async (id) => {
    try { await api.delete(`/counts/${id}`); loadCounts(accId); } catch (e) { toast.error("Silinemedi"); }
  };

  const exportStatement = (fmt) => {
    if (!accId) return;
    const params = new URLSearchParams({ format: fmt });
    if (filter.start) params.set("start", filter.start);
    if (filter.end) params.set("end", filter.end);
    if (filter.type && filter.type !== "all") params.set("type", filter.type);
    const ext = fmt === "pdf" ? "pdf" : "xlsx";
    downloadFile(`/accounts/${accId}/statement?${params.toString()}`, `kasa-${data.account?.name || "detay"}.${ext}`)
      .catch(() => toast.error("Dışa aktarılamadı"));
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Kasa Detay</h1>
          <p className="text-sm text-muted-foreground mt-1">Kasaya işlenen gelir/gider hareketlerini görüntüleyin, düzenleyin veya silin.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <Button onClick={openCount} data-testid="kasa-count-btn" variant="outline" className="border-border hover:bg-secondary" disabled={!accId}>
            <Scales size={18} className="mr-1.5" /> Kasa Sayım
          </Button>
          <div className="w-full sm:w-64">
            <Select value={accId} onValueChange={setAccId}>
              <SelectTrigger className="bg-secondary border-border" data-testid="kasa-select"><SelectValue placeholder="Kasa / hesap seçin" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Devir (Açılış)</div>
          <div className="mt-2 font-heading text-xl font-semibold font-mono-num" data-testid="kasa-opening">{formatMoney(opening, cur, currencies)}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam Gelir</div>
          <div className="mt-2 font-heading text-xl font-semibold font-mono-num text-[#10B981]" data-testid="kasa-income">+{formatMoney(income, cur, currencies)}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam Gider</div>
          <div className="mt-2 font-heading text-xl font-semibold font-mono-num text-[#EF4444]" data-testid="kasa-expense">−{formatMoney(expense, cur, currencies)}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Dönem Net Değişim</div>
          <div className={`mt-2 font-heading text-xl font-semibold font-mono-num ${periodNet >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`} data-testid="kasa-period-net">{periodNet >= 0 ? "+" : "−"}{formatMoney(Math.abs(periodNet), cur, currencies)}</div>
        </div>
        <div className="bg-card border border-primary/40 bg-primary/5 rounded-lg p-5 col-span-2 lg:col-span-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Kapanış Bakiye</div>
          <div className="mt-2 flex items-center gap-2">
            <Vault size={16} weight="fill" className="text-primary" />
            <div className="font-heading text-xl font-semibold font-mono-num" data-testid="kasa-closing">{formatMoney(closing, cur, currencies)}</div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Başlangıç</Label>
            <Input type="date" value={filter.start} onChange={(e) => applyFilter({ start: e.target.value })} className="mt-1 h-9 bg-secondary border-border text-xs" data-testid="kasa-filter-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Bitiş</Label>
            <Input type="date" value={filter.end} onChange={(e) => applyFilter({ end: e.target.value })} className="mt-1 h-9 bg-secondary border-border text-xs" data-testid="kasa-filter-end" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Tür</Label>
            <Select value={filter.type} onValueChange={(v) => applyFilter({ type: v })}>
              <SelectTrigger className="mt-1 h-9 bg-secondary border-border text-xs" data-testid="kasa-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="income">Gelir</SelectItem>
                <SelectItem value="expense">Gider</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-1">
            <Button onClick={() => exportStatement("xlsx")} data-testid="kasa-export-xlsx" variant="outline" size="sm" className="flex-1 border-border hover:bg-secondary h-9 px-2" title="Excel"><FileXls size={16} /></Button>
            <Button onClick={() => exportStatement("pdf")} data-testid="kasa-export-pdf" variant="outline" size="sm" className="flex-1 border-border hover:bg-secondary h-9 px-2" title="PDF"><FilePdf size={16} /></Button>
          </div>
        </div>

        <div className="space-y-2" data-testid="kasa-movements-list">
          {data.transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Bu kasaya ait hareket yok.</p>
          ) : data.transactions.map((t) => (
            <div key={t.id} data-testid={`kasa-movement-${t.id}`} className="flex items-center gap-3 border border-border rounded-md p-3 bg-secondary/20">
              <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${t.type === "income" ? "bg-success/10" : "bg-destructive/10"}`}>
                {t.type === "income" ? <ArrowDownLeft size={16} className="text-[#10B981]" /> : <ArrowUpRight size={16} className="text-[#EF4444]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{t.category || (t.type === "income" ? "Gelir" : "Gider")}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {t.description || "-"} · {formatDate(t.date)}
                  {t.created_by_name ? ` · ${t.created_by_name}` : ""}
                </div>
              </div>
              <div className={`font-mono-num font-semibold text-sm text-right shrink-0 ${t.type === "income" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                {t.type === "income" ? "+" : "−"}{formatMoney(t.amount, t.currency, currencies)}
              </div>
              {isEditable(t) && (
                <div className="flex gap-0.5 shrink-0">
                  <button onClick={() => openTxEdit(t)} data-testid={`kasa-movement-edit-${t.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Düzenle">
                    <PencilSimple size={15} />
                  </button>
                  <ConfirmDelete onConfirm={() => removeTx(t.id)} testId={`kasa-movement-delete-${t.id}`}
                    title="Hareketi sil?" description="Bu gelir/gider kaydı silinecek ve kasa bakiyesi güncellenecek. Bu işlem geri alınamaz.">
                    <Trash size={15} />
                  </ConfirmDelete>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!txEdit} onOpenChange={(o) => !o && setTxEdit(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Hareketi Düzenle</DialogTitle>
            <DialogDescription className="text-muted-foreground">Tutar ve açıklamayı güncelleyin. Kasa bakiyesi otomatik düzeltilir.</DialogDescription>
          </DialogHeader>
          {txEdit && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={txEdit.type} onValueChange={setTxType}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="kasa-edit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Gelir</SelectItem>
                      <SelectItem value="expense">Gider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Kategori</Label>
                  <Select value={txEdit.category} onValueChange={(v) => setTxEdit((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="kasa-edit-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catsFor(txEdit.type, txEdit.category).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                <Input type="number" value={txEdit.amount} onChange={(e) => setTxEdit((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="kasa-edit-amount" placeholder="0" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Input value={txEdit.description} onChange={(e) => setTxEdit((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="kasa-edit-description" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Tarih</Label>
                <Input type="date" value={txEdit.date} onChange={(e) => setTxEdit((f) => ({ ...f, date: e.target.value }))} className="mt-1.5 bg-secondary border-border" data-testid="kasa-edit-date" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveTxEdit} data-testid="kasa-edit-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={countOpen} onOpenChange={setCountOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Kasa Sayım (Mutabakat)</DialogTitle>
            <DialogDescription className="text-muted-foreground">Fiziksel olarak saydığınız tutarı girin; sistem bakiyesiyle karşılaştırıp kaydedin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between border border-border rounded-md px-4 py-3 bg-secondary/30">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Sistem Bakiyesi</span>
              <span className="font-heading text-lg font-semibold font-mono-num" data-testid="count-system">{formatMoney(data.account?.balance || 0, cur, currencies)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Sayılan Tutar</Label>
                <Input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="count-input" placeholder="0" autoFocus />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Not (ops.)</Label>
                <Input value={countNote} onChange={(e) => setCountNote(e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="count-note" placeholder="Örn. Gün sonu" />
              </div>
            </div>
            {counted !== "" && (() => {
              const diff = Number(counted) - (data.account?.balance || 0);
              const ok = Math.abs(diff) < 0.005;
              return (
                <div data-testid="count-result" className={`rounded-md border px-4 py-3 ${ok ? "border-[#10B981]/40 bg-[#10B981]/10" : "border-[#EF4444]/40 bg-[#EF4444]/10"}`}>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Fark</div>
                  <div className={`font-heading text-xl font-semibold font-mono-num ${ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {ok ? "Mutabık ✓" : `${diff > 0 ? "+" : "−"}${formatMoney(Math.abs(diff), cur, currencies)}`}
                  </div>
                  {!ok && <div className="text-xs text-muted-foreground mt-1">{diff > 0 ? "Kasada sistemden fazla var (fazlalık)." : "Kasada sistemden eksik var (açık)."}</div>}
                </div>
              );
            })()}
            <Button onClick={saveCount} disabled={counted === ""} data-testid="count-save" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Scales size={16} className="mr-1.5" /> Sayımı Kaydet
            </Button>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Sayım Geçmişi</div>
              <div className="max-h-[35vh] overflow-y-auto space-y-2" data-testid="count-history">
                {counts.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-3 text-center">Henüz sayım kaydı yok.</p>
                ) : counts.map((c) => {
                  const ok = Math.abs(c.difference) < 0.005;
                  return (
                    <div key={c.id} data-testid={`count-item-${c.id}`} className="flex items-center gap-3 border border-border rounded-md p-3 bg-secondary/20">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">
                          Sayılan: {formatMoney(c.counted, c.currency, currencies)}
                          <span className="text-muted-foreground font-normal"> / Sistem: {formatMoney(c.system_balance, c.currency, currencies)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {formatDate(c.created_at)} · {c.created_by_name}{c.note ? ` · ${c.note}` : ""}
                        </div>
                      </div>
                      <div className={`font-mono-num text-sm font-semibold shrink-0 ${ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                        {ok ? "✓" : `${c.difference > 0 ? "+" : "−"}${formatMoney(Math.abs(c.difference), c.currency, currencies)}`}
                      </div>
                      <ConfirmDelete onConfirm={() => removeCount(c.id)} testId={`count-delete-${c.id}`}
                        title="Sayım kaydını sil?" description="Bu mutabakat kaydı silinecek. Kasa bakiyesi etkilenmez.">
                        <Trash size={14} />
                      </ConfirmDelete>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCountOpen(false)} data-testid="count-close" variant="outline" className="border-border hover:bg-secondary">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
